import React, { useMemo } from 'react';

export default function FondClientDetailsModal({
    show,
    onClose,
    clientId,
    clientNom,
    clientPrenom,
    honoraires = [],
    chargesPayeesParFondClient = [],
    getCurrentDate
}) {
    const clientName = `${clientNom || ''} ${clientPrenom || ''}`.trim() || 'Client';

    const {
        fondsClient,
        payeParClient,
        totalFonds,
        totalPaye,
        reste
    } = useMemo(() => {
        const id = clientId != null ? Number(clientId) : null;
        const validId = id !== null && !Number.isNaN(id);
        const matchId = (v) => validId && v != null && Number(v) === id;
        const fonds = (honoraires || []).filter(h => matchId(h.client_id));
        const payes = (chargesPayeesParFondClient || []).filter(c => matchId(c.client_id));

        const totalFondsClient = fonds.reduce((sum, h) => {
            const m = parseFloat(h.avance || h.montant || 0) || 0;
            return sum + m;
        }, 0);

        const totalPayeClient = payes.reduce((sum, c) => {
            const m = parseFloat(c.montant || 0) || 0;
            return sum + m;
        }, 0);

        return {
            fondsClient: fonds,
            payeParClient: payes,
            totalFonds: totalFondsClient,
            totalPaye: totalPayeClient,
            reste: totalFondsClient - totalPayeClient
        };
    }, [clientId, honoraires, chargesPayeesParFondClient]);

    if (!show || !clientId) return null;

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatMontant = (value) => {
        const num = typeof value === 'number' ? value : parseFloat(value || 0) || 0;
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
        }).format(num);
    };

    const syntheseDate = typeof getCurrentDate === 'function' ? getCurrentDate() : '';

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;
        const rowsFonds = fondsClient.map(row =>
            `<tr><td>${formatDateForDisplay(row.date)}</td><td>${(row.libelle || '').replace(/</g, '&lt;')}</td><td class="text-end">${formatMontant(row.avance || row.montant)}</td></tr>`
        ).join('');
        const rowsPayes = payeParClient.map(row =>
            `<tr><td>${formatDateForDisplay(row.date)}</td><td>${(row.libelle || '').replace(/</g, '&lt;')}</td><td class="text-end">${formatMontant(row.montant)}</td></tr>`
        ).join('');
        const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Fond Client Chez CGM - ${(clientName || '').replace(/</g, '&lt;')}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 16px; }
    h5 { margin-bottom: 8px; }
    h6 { font-size: 14px; margin: 12px 0 6px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 12px; }
    th, td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
    th { background: #f0f0f0; }
    .text-end { text-align: right; }
    .total-row { font-weight: bold; background: #f8f8f8; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h5>Fond Client Chez CGM - ${(clientName || '').replace(/</g, '&lt;')}</h5>
  <h6>Détail des Fonds Clients Chez CGM</h6>
  <table>
    <thead><tr><th>Date</th><th>Libellé</th><th>Montant</th></tr></thead>
    <tbody>${fondsClient.length ? rowsFonds : '<tr><td colspan="3" style="text-align:center">Aucun fond client chez CGM pour ce client.</td></tr>'}</tbody>
    <tfoot><tr class="total-row"><td colspan="2">Total des Fonds Clients Chez CGM :</td><td class="text-end">${formatMontant(totalFonds)}</td></tr></tfoot>
  </table>
  <h6>Payé par Client</h6>
  <table>
    <thead><tr><th>Date</th><th>Libellé</th><th>Montant</th></tr></thead>
    <tbody>${payeParClient.length ? rowsPayes : '<tr><td colspan="3" style="text-align:center">Aucun paiement par fond client pour ce client.</td></tr>'}</tbody>
    <tfoot><tr class="total-row"><td colspan="2">Total Payé par Fond Client :</td><td class="text-end">${formatMontant(totalPaye)}</td></tr></tfoot>
  </table>
  <h6>Synthèse</h6>
  <table>
    <thead><tr><th>Date</th><th>Total Fond Client</th><th>Total Payé par Client</th><th>Reste CGM</th></tr></thead>
    <tbody><tr><td>${(syntheseDate || '').replace(/</g, '&lt;')}</td><td class="text-end">${formatMontant(totalFonds)}</td><td class="text-end">${formatMontant(totalPaye)}</td><td class="text-end">${formatMontant(reste)}</td></tr></tbody>
  </table>
</body>
</html>`;
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        const doPrint = () => {
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
        };
        if (printWindow.document.readyState === 'complete') {
            setTimeout(doPrint, 100);
        } else {
            printWindow.onload = () => setTimeout(doPrint, 100);
        }
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered" style={{ zIndex: 1061 }}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-wallet me-2"></i>
                            Fond Client Chez CGM - {clientName}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <h6 className="etat-modal-h6 text-dark fw-bold">Détail des Fonds Clients Chez CGM</h6>
                        <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered etat-modal-table">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border">Date</th>
                                        <th className="border">Libellé</th>
                                        <th className="border">Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fondsClient.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="border text-center text-muted">
                                                Aucun fond client chez CGM pour ce client.
                                            </td>
                                        </tr>
                                    ) : fondsClient.map((row, idx) => (
                                        <tr key={idx}>
                                            <td className="border">{formatDateForDisplay(row.date)}</td>
                                            <td className="border">{row.libelle}</td>
                                            <td className="border text-end fw-bold text-success">
                                                {formatMontant(row.avance || row.montant)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-light">
                                    <tr>
                                        <th className="border text-end" colSpan={2}>Total des Fonds Clients Chez CGM :</th>
                                        <th className="border text-end text-success">
                                            {formatMontant(totalFonds)}
                                        </th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <h6 className="etat-modal-h6 text-dark fw-bold">Payé par Client</h6>
                        <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered etat-modal-table">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border">Date</th>
                                        <th className="border">Libellé</th>
                                        <th className="border">Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payeParClient.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="border text-center text-muted">
                                                Aucun paiement par fond client pour ce client.
                                            </td>
                                        </tr>
                                    ) : payeParClient.map((row, idx) => (
                                        <tr key={idx}>
                                            <td className="border">{formatDateForDisplay(row.date)}</td>
                                            <td className="border">{row.libelle}</td>
                                            <td className="border text-end fw-bold text-danger">
                                                {formatMontant(row.montant)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-light">
                                    <tr>
                                        <th className="border text-end" colSpan={2}>Total Payé par Fond Client :</th>
                                        <th className="border text-end text-danger">
                                            {formatMontant(totalPaye)}
                                        </th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <h6 className="etat-modal-h6 text-dark fw-bold">Synthèse</h6>
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered etat-modal-table">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border">Date</th>
                                        <th className="border">Total Fond Client</th>
                                        <th className="border">Total Payé par Fond Client</th>
                                        <th className="border">Reste CGM</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="border">{syntheseDate}</td>
                                        <td className="border text-end text-success fw-bold">
                                            {formatMontant(totalFonds)}
                                        </td>
                                        <td className="border text-end text-danger fw-bold">
                                            {formatMontant(totalPaye)}
                                        </td>
                                        <td className="border text-end fw-bold">
                                            {formatMontant(reste)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary btn-sm me-2" onClick={handlePrint} title="Imprimer l'état pour ce client">
                            <i className="fas fa-print me-1"></i>Imprimer
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

